import { BrushMask } from "@/core/types"
import { MappedMask } from "@/store/editor/mask-utils"
import { PropsWithChildren, useContext, useEffect, useRef, useState } from "react"
import { CanvasControlsOverlay } from "../canvas-controls/CanvasControls"
import { usePointerDrag } from "@/utils/usePointerDrag"
import {
	mapImageOutputPointsToStable,
	unprojectPointerCoord,
} from "../canvas-controls/canvas-controls-utils"
import { CanvasControlsContext } from "../canvas-controls/CanvasControlsContext"
import {
	showingFloatingMaskingPanelAtom,
	showingMaskingToolsOverlayAtom,
	updateMask,
} from "@/store/editor/masks-state"
import { saveCurrentAdjustmentState } from "@/store/store-history"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useEditor } from "@/utils/useEditor"
import {
	BrushCursor,
	BrushCursorName,
	BrushStroke,
	addPointToBrushStroke,
	createEmptyBrushStroke,
} from "@/core/utils/brushing"
import { currentCursorPresetNameAtom, cursorAtom } from "@/store/brush-cursor-state"
import { maskOverlaySettingsAtom } from "@/store/editor/mask-overlays"
import { clamp, isVec2InRange } from "@/utils/math"
import { viewportCanvasSettingsAtom } from "@/store/ui-views-state"
import {
	healingAiPendingAtom,
	healingModeAtom,
	healingCompleteCurrentStroke,
	selectedHealingAtom,
} from "../../store/editor/healing-state"
import { vec2 } from "gl-matrix"
import { healingSizeToSliderValue, sliderValueToHealingSize } from "@/core/utils/brushing"
import { debounce } from "@/utils/debounce"
import { useShortcuts } from "@/utils/useShortcuts"
import { actionShortcuts } from "@/shortcuts/shortcuts"

interface CursorDrawPosition {
	x: number
	y: number
}

interface CursorDrawViewport {
	width: number
	height: number
}

type CursorDrawPositionResolver = (viewport: CursorDrawViewport) => CursorDrawPosition

const BrushMaskCursorControl = (props: {
	x: number
	y: number
	innerRadius: number
	outerRadius: number
	isErasing: boolean
	scale: number
	drawAt?: CursorDrawPosition
}) => {
	const editor = useEditor()
	const { parentWidth, parentHeight } = useContext(CanvasControlsContext)

	// check if the cursor is not ready
	const propsInnerRadius = Number.isNaN(props.innerRadius) ? 1 : props.innerRadius
	const propsOuterRadius = Number.isNaN(props.outerRadius) ? 1 : props.outerRadius

	let innerRadius = (propsInnerRadius * Math.min(parentWidth, parentHeight)) / props.scale - 2 // -2 to fit the stroke's size
	let outerRadius = (propsOuterRadius * Math.min(parentWidth, parentHeight)) / props.scale - 2 // -2 to fit the stroke's size
	innerRadius = Math.max(0, innerRadius)
	outerRadius = Math.max(0, outerRadius)

	const drawAt = (x: number, y: number) => {
		// The regular cursor is inside the rotated image-space controls group.
		// Keep its pointer glyph screen-aligned, as Pin does, while leaving the
		// already-screen-space size preview unchanged.
		const pointerTransform =
			props.drawAt === undefined ? `rotate(${-editor.rotation} ${x} ${y})` : undefined

		const renderDrawingPointer = () => {
			const length = 4

			return (
				<>
					{/* Black outer edge - slightly wider */}
					<line
						x1={x - length}
						y1={y}
						x2={x + length}
						y2={y}
						stroke="rgba(0,0,0,0.5)"
						strokeWidth={1.6}
					/>
					<line
						x1={x}
						y1={y - length}
						x2={x}
						y2={y + length}
						stroke="rgba(0,0,0,0.5)"
						strokeWidth={1.6}
					/>
					{/* White center */}
					<line
						x1={x - length}
						y1={y}
						x2={x + length}
						y2={y}
						stroke="white"
						strokeWidth={0.8}
					/>
					<line
						x1={x}
						y1={y - length}
						x2={x}
						y2={y + length}
						stroke="white"
						strokeWidth={0.8}
					/>
				</>
			)
		}

		const renderErasingPointer = () => {
			const length = 4

			return (
				<>
					<ellipse
						cx={x}
						cy={y}
						rx={length + 4}
						ry={length + 4}
						fill="rgba(0, 0, 0, 0.2)"
						className="pointer-events-none"
					/>
					{/* Black outer edge - slightly wider */}
					<line
						x1={x - length}
						y1={y}
						x2={x + length}
						y2={y}
						stroke="rgba(0,0,0,0.5)"
						strokeWidth={1.6}
					/>
					{/* White center */}
					<line
						x1={x - length}
						y1={y}
						x2={x + length}
						y2={y}
						stroke="white"
						strokeWidth={0.8}
					/>
				</>
			)
		}

		return (
			<g>
				{/* External feathering ellipse - white with black outer edge */}
				<ellipse
					cx={x}
					cy={y}
					rx={outerRadius + 0.6}
					ry={outerRadius + 0.6}
					stroke="rgba(0, 0, 0, 0.2)"
					strokeWidth={1}
					fill="none"
					className="pointer-events-none"
				/>
				<ellipse
					cx={x}
					cy={y}
					rx={outerRadius}
					ry={outerRadius}
					stroke="rgba(255, 255, 255, 0.3)"
					strokeWidth={1}
					fill="none"
					className="pointer-events-none"
				/>
				{/* Internal radius ellipse - white with black outer edge */}
				<ellipse
					cx={x}
					cy={y}
					rx={innerRadius + 0.6}
					ry={innerRadius + 0.6}
					stroke="rgba(0, 0, 0, 0.4)"
					strokeWidth={1}
					fill="none"
					className="pointer-events-none"
				/>
				<ellipse
					cx={x}
					cy={y}
					rx={innerRadius}
					ry={innerRadius}
					stroke="rgba(255, 255, 255, 0.7)"
					strokeWidth={1}
					fill="none"
					className="pointer-events-none"
				/>
				{/* Pointers */}
				<g transform={pointerTransform}>
					{props.isErasing == true ? renderErasingPointer() : renderDrawingPointer()}
				</g>
			</g>
		)
	}

	return drawAt(
		props.drawAt?.x ?? props.x * parentWidth,
		props.drawAt?.y ?? props.y * parentHeight,
	)
}

type BrushControlType = "mask" | "healing"

export const BrushMaskControls = (
	props: PropsWithChildren<{
		mask?: MappedMask<BrushMask>
		hidden: boolean
		hideCursor?: boolean
		cursorDrawPosition?: CursorDrawPosition | CursorDrawPositionResolver
	}>,
) => {
	const editor = useEditor()
	const brushControlType: BrushControlType = props.mask ? "mask" : "healing"
	const [activeStroke, setActiveStroke] = useState<BrushStroke | null>(null)
	const activeStrokeRef = useRef<BrushStroke | null>(null)
	const maskStrokesRef = useRef<BrushStroke[] | null>(null)
	const [cursor, setCursor] = useAtom(cursorAtom)
	const [lastCursorName, setLastCursorName] = useState<BrushCursorName>(cursor.name)
	const setCurrentCursorPresetName = useSetAtom(currentCursorPresetNameAtom)
	const [viewportCanvasSettings, setViewportCanvasSettings] = useAtom(viewportCanvasSettingsAtom)
	const setShowingMaskingOverlay = useSetAtom(showingMaskingToolsOverlayAtom)
	// for mask brush only
	const setShowingFloatingMaskingPanel = useSetAtom(showingFloatingMaskingPanelAtom)
	const [maskOverlaySettings, setMaskOverlaySettings] = useAtom(maskOverlaySettingsAtom)
	const setSelectedHealing = useSetAtom(selectedHealingAtom)
	const [inZoomMode, setInZoomMode] = useState(false)
	const enableTouchZoom = brushControlType === "healing"
	const healingMode = useAtomValue(healingModeAtom)
	const isAiPending = useAtomValue(healingAiPendingAtom)

	const mask = props.mask?.properties
	if (brushControlType === "healing") {
		if (cursor.name !== "healing") {
			setCurrentCursorPresetName("healing")
		}
	} else {
		if (cursor.name === "healing") {
			setCurrentCursorPresetName("brush")
		}
	}

	useEffect(() => {
		setViewportCanvasSettings({
			isZoomableWithZ: true,
			isZoomableWithSpace: false,
			isZoomableWithMouse: false,
			isDraggableWithMouse: false,
			isZoomableWithTouchpadOnly: enableTouchZoom,
		})

		return () => {
			setViewportCanvasSettings({
				isZoomableWithZ: true,
				isZoomableWithSpace: true,
				isZoomableWithMouse: true,
				isDraggableWithMouse: true,
				isZoomableWithTouchpadOnly: false,
			})
		}
	}, [])

	useShortcuts({
		keys: [actionShortcuts.space],
		action: (event) => {
			if (event.repeat) {
				return
			}

			if (event.type === "keydown") {
				setViewportCanvasSettings({
					...viewportCanvasSettings,
					isZoomableWithMouse: true,
					isDraggableWithMouse: true,
				})
				setInZoomMode(true)
			} else {
				setViewportCanvasSettings({
					...viewportCanvasSettings,
					isZoomableWithMouse: false,
					isDraggableWithMouse: false,
				})
				setInZoomMode(false)
			}
		},
		trackingLabel: "brush_switch_zoom_mode",
		preventDefault: true,
		keydown: true,
		keyup: true,
	})

	useShortcuts({
		keys: [actionShortcuts.brushSwitchCursor],
		action: (event) => {
			if (event.repeat) {
				return
			}

			if (brushControlType === "healing") {
				return
			}

			if (event.type === "keydown") {
				if (activeStroke !== null) {
					//TODO: @din figure out a way to allow switching cursors in the middle of dragging
					return
				}

				setLastCursorName(cursor.name)
				setCurrentCursorPresetName(cursor.name == "brush" ? "erase" : "brush")
			} else {
				setCurrentCursorPresetName(lastCursorName)
			}
		},
		trackingLabel: "brush_switch_cursor",
		keyup: true,
		keydown: true,
		preventDefault: true,
	})

	useShortcuts({
		keys: [actionShortcuts.brushSizeMinus, actionShortcuts.brushSizePlus],
		action: (event) => {
			switch (event.key) {
				case actionShortcuts.brushSizeMinus:
					adjustBrushSize(-0.01)
					break
				case actionShortcuts.brushSizePlus:
					adjustBrushSize(0.01)
					break
			}
		},
		trackingLabel: "brush_adjust_size",
	})

	async function handlePointerUp() {
		const hasActiveStroke = brushControlType === "mask" ? activeStrokeRef.current : activeStroke

		if (brushControlType === "mask") {
			setMaskOverlaySettings({ ...maskOverlaySettings, previewAdjustedMask: false })
			setShowingFloatingMaskingPanel(true)
		}

		if (hasActiveStroke) {
			if (brushControlType === "healing") {
				if ((healingMode === "ai-generate" || healingMode === "ai-erase") && isAiPending) {
					setActiveStroke(null)
					editor.renderer.brushMaskPass.setAdditiveMode(false)
					return
				}
				// Keep the live overlay in output space. Pass a stable-space copy directly
				// to completion so it cannot be rendered by the output-space overlay path.
				const currentStroke = editor.healingCurrentStroke
				healingCompleteCurrentStroke(
					currentStroke
						? {
								...currentStroke,
								points: mapImageOutputPointsToStable(currentStroke.points),
							}
						: undefined,
				)
			} else {
				await saveCurrentAdjustmentState({
					message: `Update mask`,
					changedValue: mask?.name ?? "stroke",
				})
			}
			setActiveStroke(null)
			activeStrokeRef.current = null
			maskStrokesRef.current = null

			editor.renderer.brushMaskPass.setAdditiveMode(false)
			editor.render({ reason: "end-brush-stroke" })
		}
	}

	const bindDrag = usePointerDrag<SVGElement>({
		onPointerMove: (e) => {
			setShowingMaskingOverlay(true)
			const coords = unprojectPointerCoord(e.pageX, e.pageY)

			setCursor({
				...cursor,
				x: coords[0],
				y: coords[1],
			})

			if (
				(brushControlType === "mask" ? activeStrokeRef.current : activeStroke) &&
				e.buttons === 0
			) {
				debounce(handlePointerUp, 10)()
			}
		},
		onPointerOut: (e) => {
			const nextTarget = e.relatedTarget
			if (nextTarget instanceof Node && e.currentTarget.contains(nextTarget)) {
				return
			}
			setShowingMaskingOverlay(false)
		},
		onPointerDown: (e) => {
			if (inZoomMode) {
				return
			}

			const coords = unprojectPointerCoord(e.pageX, e.pageY)
			if (!isVec2InRange(coords, 0, 1)) {
				return
			}
			const nextCursor = {
				...cursor,
				x: coords[0],
				y: coords[1],
			}
			setCursor(nextCursor)

			if (brushControlType === "healing") {
				if ((healingMode === "ai-generate" || healingMode === "ai-erase") && isAiPending) {
					return
				}
				setSelectedHealing(null)
			}

			editor.renderer.brushMaskPass.setAdditiveMode(true)

			const strokeScale = brushControlType === "mask" ? editor.mainViewport.relativeZoom : 1.0
			const stroke = createEmptyBrushStroke(nextCursor, strokeScale)

			switch (brushControlType) {
				case "mask": {
					const maskStrokes = (mask as BrushMask).strokes.slice()
					maskStrokes.push(stroke)
					const nextStroke = addPointToBrushStroke(
						stroke,
						nextCursor,
						editor.mainViewport.textureSize,
					)
					setActiveStroke(nextStroke)
					activeStrokeRef.current = nextStroke
					maskStrokesRef.current = maskStrokes
					updateMask(props.mask as MappedMask<BrushMask>, { strokes: maskStrokes })
					break
				}
				case "healing": {
					if (
						(healingMode === "ai-generate" || healingMode === "ai-erase") &&
						isAiPending
					) {
						return
					}
					if (!editor.renderer.healingPass.enabled) {
						editor.renderer.healingPass.enabled = true
					}
					stroke.isColorAware = false // turn off color aware for healing
					setActiveStroke(
						addPointToBrushStroke(
							stroke,
							cursor,
							editor.mainViewport.textureSize,
							true,
						),
					)
					editor.healingCurrentStroke = {
						...stroke,
						points: stroke.points.slice(),
					}
					break
				}
			}
		},
		onPointerUp: () => {
			if (inZoomMode && brushControlType !== "healing") {
				return
			}
			debounce(handlePointerUp)()
		},
		onDrag: (drag) => {
			if (inZoomMode) {
				return
			}
			if (brushControlType === "mask") {
				setMaskOverlaySettings({ ...maskOverlaySettings, previewAdjustedMask: true })
				setShowingFloatingMaskingPanel(false)
			}

			const coords = unprojectPointerCoord(drag.x, drag.y)

			const nextCursor = {
				...cursor,
				x: coords[0],
				y: coords[1],
			}
			setCursor(nextCursor)

			switch (brushControlType) {
				case "mask": {
					const currentStroke = activeStrokeRef.current
					if (!currentStroke) {
						break
					}

					const nextStroke = addPointToBrushStroke(
						currentStroke,
						nextCursor,
						editor.mainViewport.textureSize,
					)
					activeStrokeRef.current = nextStroke
					const maskStrokes = maskStrokesRef.current ?? [
						...(mask as BrushMask).strokes,
						nextStroke,
					]
					maskStrokesRef.current = maskStrokes
					setActiveStroke(nextStroke)
					updateMask(props.mask as MappedMask<BrushMask>, { strokes: maskStrokes })
					break
				}
				case "healing": {
					if (
						(healingMode === "ai-generate" || healingMode === "ai-erase") &&
						isAiPending
					) {
						return
					}
					if (!activeStroke) {
						break
					}

					const nextStroke = addPointToBrushStroke(
						activeStroke,
						nextCursor,
						editor.mainViewport.textureSize,
						true,
					)
					setActiveStroke(nextStroke)
					editor.healingCurrentStroke = {
						...nextStroke,
						points: nextStroke.points.slice(),
					}
					break
				}
			}
		},
	})

	const handleWheel = (e: React.WheelEvent) => {
		if (inZoomMode) {
			return
		}

		adjustBrushSize(-e.deltaY / 20000)
	}

	const adjustBrushSize = (delta: number) => {
		let newRadius = cursor.radius + delta
		let range: vec2 = [0.01, 1]

		if (brushControlType === "healing") {
			const newSlider = healingSizeToSliderValue(cursor.radius) + delta
			newRadius = sliderValueToHealingSize(newSlider)
			range = [sliderValueToHealingSize(0.01), sliderValueToHealingSize(1)]
		}

		setCursor({ ...cursor, radius: clamp(newRadius, ...range) } as BrushCursor)
	}

	const renderBrushCursor = (drawAt?: CursorDrawPosition) => (
		<BrushMaskCursorControl
			isErasing={cursor.isErasing}
			x={cursor.x}
			y={cursor.y}
			innerRadius={
				brushControlType === "mask"
					? cursor.radius / 2
					: Math.max(0, cursor.radius / 2 - (cursor.radius / 2) * cursor.feather * 0.5)
			}
			outerRadius={
				brushControlType === "mask"
					? cursor.radius / 2 + 0.001 + (cursor.radius / 2) * cursor.feather * 0.8
					: cursor.radius / 2
			}
			scale={brushControlType === "mask" ? editor.mainViewport.relativeZoom : 1.0}
			drawAt={drawAt}
		/>
	)
	const shouldDrawScreenSpaceCursor = props.cursorDrawPosition !== undefined && !inZoomMode

	return (
		<CanvasControlsOverlay
			onWheel={handleWheel}
			className={!inZoomMode ? "cursor-none" : ""}
			screenSpaceChildren={
				shouldDrawScreenSpaceCursor
					? (viewport) =>
							renderBrushCursor(
								typeof props.cursorDrawPosition === "function"
									? props.cursorDrawPosition(viewport)
									: props.cursorDrawPosition,
							)
					: undefined
			}
			{...bindDrag}
		>
			{!inZoomMode &&
				props.hideCursor !== true &&
				!shouldDrawScreenSpaceCursor &&
				renderBrushCursor()}
			{props.children}
		</CanvasControlsOverlay>
	)
}
